// Reference: blueprint:javascript_stripe integration
import { useStripe, Elements, PaymentElement, useElements } from '@stripe/react-stripe-js';
import { stripePromise } from '@/lib/stripe';
import { useState, useEffect } from 'react';
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Check, Rocket, Tag, AlertCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTerritory } from "@/contexts/TerritoryContext";
import { useLocation } from "wouter";
import { trackEvent } from "@/lib/mixpanel";
import { capturePostHogEvent } from "@/lib/posthog";
import { useQuery } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";


const SubscribeForm = ({ selectedPlan, selectedGrade, setupIntentId, onSuccess }: { 
  selectedPlan: string; 
  selectedGrade: number;
  setupIntentId: string;
  onSuccess: () => void;
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const { toast } = useToast();
  const { territory } = useTerritory();
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [discountCode, setDiscountCode] = useState('');
  const [validatedDiscount, setValidatedDiscount] = useState<{ type: string; value: number } | null>(null);
  const [validatedVoucher, setValidatedVoucher] = useState<{ plan: string; durationDays?: number; kind?: string } | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [trialLinkApplied, setTrialLinkApplied] = useState(false);
  // Offers with a per-recipient deadline ride on a signed token from the emailed link. It has to
  // travel with the code to validation and to checkout, or the offer is refused.
  const [offerToken, setOfferToken] = useState<string | null>(null);
  const [offerDeadline, setOfferDeadline] = useState<string | null>(null);
  // Tracks the Stripe PaymentElement's underlying iframe readiness. Stripe will
  // throw "We could not retrieve data from the specified Element" if we call
  // confirmSetup() before this fires, so we use it both to gate the submit
  // button and as a defensive guard inside the handler.
  const [paymentElementReady, setPaymentElementReady] = useState(false);
  const [, navigate] = useLocation();
  const [duplicateCardInfo, setDuplicateCardInfo] = useState<{ existingAccountMaskedEmail: string; setupIntentId: string } | null>(null);

  // Auto-apply pending trial code from trial link, or an offer code carried in the URL.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlCode = params.get("code");
    const urlToken = params.get("o");
    const raw = localStorage.getItem("edufocal_pending_trial");
    if (!raw) {
      if (urlCode) {
        setDiscountCode(urlCode.toUpperCase());
        setOfferToken(urlToken);
      }
      return;
    }
    try {
      const pending = JSON.parse(raw);
      const ageMs = Date.now() - (pending.savedAt || 0);
      if (ageMs > 24 * 60 * 60 * 1000) {
        localStorage.removeItem("edufocal_pending_trial");
        if (urlCode) { setDiscountCode(urlCode.toUpperCase()); setOfferToken(urlToken); }
        return;
      }
      setDiscountCode(pending.code);
      setValidatedDiscount({ type: pending.type, value: pending.value });
      setOfferToken(pending.offerToken ?? urlToken ?? null);
      setOfferDeadline(pending.offerDeadline ?? null);
      setTrialLinkApplied(true);
    } catch {
      localStorage.removeItem("edufocal_pending_trial");
    }
  }, []);

  // Handle redirect when success is shown
  useEffect(() => {
    if (showSuccess) {
      localStorage.removeItem("edufocal_pending_trial");
      const timer = setTimeout(() => {
        onSuccess();
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [showSuccess, onSuccess]);

  const validateDiscountCode = async () => {
    if (!discountCode.trim()) {
      return;
    }

    setIsValidating(true);
    try {
      // Try voucher validation first
      const voucherRes = await fetch("/api/vouchers/validate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: discountCode.trim() }),
      });
      const voucherData = await voucherRes.json();

      if (voucherData.valid) {
        setValidatedVoucher({ plan: voucherData.plan, durationDays: voucherData.durationDays, kind: voucherData.kind });
        setValidatedDiscount(null);
        toast({
          title: "Voucher Applied",
          description: `${voucherData.durationDays ? `${voucherData.durationDays} days of` : (voucherData.plan === 'annual' ? 'Annual' : 'Monthly')} access, no payment required`,
        });
        capturePostHogEvent('Voucher Code Applied', {
          voucher_plan: voucherData.plan,
        });
        return;
      }

      // Not a voucher: fall back to discount code validation
      const response = await apiRequest("POST", "/api/discount-codes/validate", {
        code: discountCode.trim(),
        plan: selectedPlan,
        offerToken: offerToken || undefined,
      });

      const data = await response.json();
      setValidatedDiscount(data);
      setOfferDeadline(data.offerDeadline ?? null);
      setValidatedVoucher(null);
      toast({
        title: "Discount Applied",
        description: data.type === 'percentage'
          ? `${data.value}% discount applied`
          : `${data.value} day free trial applied`,
      });
      capturePostHogEvent('Discount Code Applied', {
        discount_type: data.type,
        discount_value: data.value,
      });
    } catch (error: any) {
      toast({
        title: "Invalid Code",
        description: error.message || "This code is not valid",
        variant: "destructive",
      });
      setValidatedDiscount(null);
      setValidatedVoucher(null);
    } finally {
      setIsValidating(false);
    }
  };

  // Wraps the POST /api/confirm-subscription call and handles the duplicate-card
  // guard. Returns the parsed JSON on success, or `null` if the server signaled
  // a duplicate-card 409 (in which case the dialog has been opened for the user
  // to decide).
  const postConfirmSubscription = async (confirmedSetupIntentId: string, duplicateCardAcknowledged: boolean) => {
    try {
      const response = await apiRequest("POST", "/api/confirm-subscription", {
        setupIntentId: confirmedSetupIntentId,
        plan: selectedPlan,
        grade: selectedGrade,
        discountCode: validatedDiscount ? discountCode.trim() : undefined,
        offerToken: validatedDiscount && offerToken ? offerToken : undefined,
        duplicateCardAcknowledged,
      });
      return await response.json();
    } catch (err: any) {
      if (err?.status === 409 && (err?.message === "duplicate_card" || err?.response?.error === "duplicate_card")) {
        const masked = err?.response?.existingAccountMaskedEmail as string | undefined;
        if (masked) {
          setDuplicateCardInfo({ existingAccountMaskedEmail: masked, setupIntentId: confirmedSetupIntentId });
          return null;
        }
      }
      throw err;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setIsProcessing(true);

    try {
      // Handle voucher redemption: bypass Stripe entirely
      if (validatedVoucher) {
        const redeemRes = await apiRequest("POST", "/api/vouchers/redeem", {
          code: discountCode.trim(),
        });
        const redeemData = await redeemRes.json();

        if (redeemData.success) {
          trackEvent('Voucher Redeemed', { plan: validatedVoucher.plan });
          capturePostHogEvent('Voucher Redeemed', { plan: validatedVoucher.plan });
          await queryClient.invalidateQueries({ queryKey: ['/api/auth/me'] });
          setShowSuccess(true);
        } else {
          throw new Error(redeemData.error || "Failed to redeem voucher");
        }
        setIsProcessing(false);
        return;
      }

      // Check if this is a free subscription (100% discount)
      const isFreeSubscription = validatedDiscount?.type === 'percentage' && validatedDiscount.value === 100;

      let confirmedSetupIntentId = setupIntentId;

      if (!isFreeSubscription) {
        // For paid subscriptions, confirm the SetupIntent with Stripe
        if (!stripe || !elements) {
          setIsProcessing(false);
          return;
        }

        // Guard against an "Element not mounted" race: if the PaymentElement
        // hasn't fired its ready event yet, defer to its built-in submit
        // gate. elements.submit() returns an error if the iframe isn't ready
        // and resolves cleanly once it is, so we await it before confirmSetup.
        if (!paymentElementReady) {
          const { error: submitError } = await elements.submit();
          if (submitError) {
            toast({
              title: "Payment form still loading",
              description: submitError.message || "Please give it a second and try again.",
              variant: "destructive",
            });
            setIsProcessing(false);
            return;
          }
        }

        // Step 1: Confirm the SetupIntent (collect payment method)
        const { error, setupIntent } = await stripe.confirmSetup({
          elements,
          redirect: 'if_required',
        });

        if (error) {
          toast({
            title: "Payment Method Failed",
            description: error.message,
            variant: "destructive",
          });
          setIsProcessing(false);
          return;
        }

        if (setupIntent.status !== 'succeeded') {
          toast({
            title: "Payment Method Failed",
            description: "Could not confirm payment method",
            variant: "destructive",
          });
          setIsProcessing(false);
          return;
        }

        confirmedSetupIntentId = setupIntent.id;
      }

      // Step 2: Confirm the subscription with the backend
      const data = await postConfirmSubscription(confirmedSetupIntentId, false);
      if (!data) {
        // A duplicate-card guard triggered; the dialog is now up and waiting on the user.
        setIsProcessing(false);
        return;
      }

      if (data.success) {
        // Track subscription event
        trackEvent('Subscription Created', {
          plan: selectedPlan,
          grade: selectedGrade,
        });
        capturePostHogEvent('Subscription Created', {
          plan: selectedPlan,
          grade: selectedGrade,
        });
        
        // Refresh user data so subscription status is updated
        await queryClient.invalidateQueries({ queryKey: ['/api/auth/me'] });
        
        // Show success animation (redirect handled by useEffect)
        setShowSuccess(true);
      } else if (data.pending) {
        // Payment requires additional action (3DS) or is processing
        if (data.requiresAction && data.clientSecret && stripe) {
          // Handle 3D Secure authentication
          toast({
            title: "Additional Authentication Required",
            description: "Completing payment authentication...",
          });
          
          const { error: confirmError } = await stripe.confirmCardPayment(data.clientSecret);
          
          if (confirmError) {
            throw new Error(confirmError.message || "Payment authentication failed");
          }
          
          // Authentication completed, retry subscription confirmation
          await new Promise(resolve => setTimeout(resolve, 2000));

          const retryData = await postConfirmSubscription(confirmedSetupIntentId, false);
          if (!retryData) {
            // Duplicate-card guard triggered; dialog is up.
            setIsProcessing(false);
            return;
          }

          if (retryData.success) {
            // Refresh user data so subscription status is updated
            await queryClient.invalidateQueries({ queryKey: ['/api/auth/me'] });
            
            // Show success animation (redirect handled by useEffect)
            setShowSuccess(true);
          } else {
            throw new Error(retryData.message || "Failed to activate subscription after authentication");
          }
        } else if (data.processing) {
          // Payment is being processed asynchronously (e.g. bank-based card).
          // The subscription is already created on Stripe's side: do NOT retry
          // confirm-subscription here as that would create a second charge.
          // The webhook will activate the subscription once payment clears.
          toast({
            title: "Payment Processing",
            description: "Your payment is being verified. You'll get access as soon as it clears, usually within a few minutes.",
          });
          await queryClient.invalidateQueries({ queryKey: ['/api/auth/me'] });
          setShowSuccess(true);
        } else {
          throw new Error(data.message || "Payment is pending");
        }
      } else {
        throw new Error(data.message || "Failed to activate subscription");
      }
    } catch (error: any) {
      // ApiError includes structured error response data
      const errorMessage = error.response?.message || error.message || "";
      const errorStatus = error.status;
      
      // 402 indicates payment decline
      if (errorStatus === 402 || errorMessage.includes("card was declined") || errorMessage.includes("Payment failed")) {
        toast({
          title: "Payment Declined",
          description: errorMessage || "Your card was declined. Please try a different payment method.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Subscription Error",
          description: errorMessage || "An error occurred during subscription",
          variant: "destructive",
        });
      }
    } finally {
      setIsProcessing(false);
    }
  };

  if (showSuccess) {
    return (
      <div className="flex flex-col items-center justify-center py-12 space-y-6">
        <div className="relative">
          <Rocket 
            className="w-24 h-24 text-primary animate-bounce" 
            style={{
              animation: 'rocketLaunch 2s ease-in-out'
            }}
          />
        </div>
        <div className="text-center space-y-3">
          <h2 className="text-3xl font-bold text-primary" data-testid="text-success-title">
            Welcome Aboard!
          </h2>
          <p className="text-xl text-muted-foreground" data-testid="text-success-message">
            Your subscription is now active!
          </p>
          <p className="text-sm text-muted-foreground">
            Taking you to your dashboard...
          </p>
        </div>
      </div>
    );
  }

  const prices = {
    monthly: 2500,
    yearly: 10000,
  };

  const basePrice = prices[selectedPlan as 'monthly' | 'yearly'];
  const discountedPrice = validatedDiscount && validatedDiscount.type === 'percentage'
    ? basePrice - (basePrice * validatedDiscount.value / 100)
    : basePrice;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {trialLinkApplied && validatedDiscount ? (
        <div className="p-4 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg space-y-1">
          <div className="flex items-center gap-2 font-semibold text-green-800 dark:text-green-200">
            <Check className="w-4 h-4" />
            {/* Type-aware. This banner used to read "{value}-Day Free Trial Applied" for every
                code that arrived through a link, so PEPCHECK, a 50 percent discount, announced
                "50-Day Free Trial Applied. No payment will be charged" directly above a summary
                charging J$1,250. Promising a parent free access and then taking their money is
                the worst thing this page could do. */}
            {validatedDiscount.type === 'trial'
              ? `${validatedDiscount.value}-Day Free Trial Applied`
              : `${validatedDiscount.value}% Off Your First Month Applied`}
          </div>
          <p className="text-sm text-green-700 dark:text-green-300">
            {validatedDiscount.type === 'trial'
              ? "Your free trial was automatically activated via your invite link. No payment will be charged during the trial period."
              : "Your discount was applied automatically from your link. It comes off your first month, and the usual price applies after that. Cancel any time."}
          </p>
        </div>
      ) : (
        <div className="space-y-4 p-4 bg-muted/30 rounded-lg">
          <div className="flex items-center justify-between">
            <Label className="text-base font-semibold">Have a discount / voucher code?</Label>
            {(validatedDiscount || validatedVoucher) && (
              <Badge variant="secondary" className="gap-1">
                <Tag className="w-3 h-3" />
                Applied
              </Badge>
            )}
          </div>
          <div className="flex gap-2">
            <Input
              type="text"
              placeholder="Enter code"
              value={discountCode}
              onChange={(e) => {
                setDiscountCode(e.target.value.toUpperCase());
                // A typed code is not the one we emailed, so its deadline token no longer applies.
                setOfferToken(null);
                setOfferDeadline(null);
              }}
              disabled={isValidating || !!validatedDiscount || !!validatedVoucher}
              className="flex-1"
              data-testid="input-discount-code"
            />
            <Button
              type="button"
              onClick={validateDiscountCode}
              disabled={!discountCode.trim() || isValidating || !!validatedDiscount || !!validatedVoucher}
              variant="outline"
              data-testid="button-apply-discount"
            >
              {isValidating ? "Checking..." : (validatedDiscount || validatedVoucher) ? "Applied" : "Apply"}
            </Button>
          </div>
          {validatedVoucher && (
            <div className="space-y-2 pt-2 border-t">
              <div className="flex justify-between font-semibold text-primary">
                <span>Voucher:</span>
                <span>{validatedVoucher.durationDays ? `${validatedVoucher.durationDays} Days` : (validatedVoucher.plan === 'annual' ? '1 Year' : '30 Days')} Access</span>
              </div>
              <p className="text-sm text-muted-foreground">No payment required: your voucher covers the full cost.</p>
            </div>
          )}
          {validatedDiscount && (
            <div className="space-y-2 pt-2 border-t">
              {validatedDiscount.type === 'percentage' ? (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Original Price:</span>
                    <span className="line-through">{territory.currency} ${basePrice.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Discount ({validatedDiscount.value}%):</span>
                    <span className="text-green-600">-{territory.currency} ${(basePrice - discountedPrice).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between font-semibold">
                    <span>New Price:</span>
                    <span className="text-primary">{territory.currency} ${discountedPrice.toLocaleString()}</span>
                  </div>
                </>
              ) : (
                <div className="flex justify-between font-semibold text-primary">
                  <span>Free Trial:</span>
                  <span>{validatedDiscount.value} days</span>
                </div>
              )}
              {offerDeadline && (
                <p className="text-xs text-muted-foreground" data-testid="text-offer-deadline">
                  This offer closes {offerDeadline}.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {!validatedVoucher && (
        <PaymentElement
          options={{
            fields: {
              billingDetails: {
                address: 'auto',
              }
            }
          }}
          onReady={() => setPaymentElementReady(true)}
        />
      )}

      <Button
        type="submit"
        disabled={
          (!validatedVoucher && (!stripe || !paymentElementReady)) ||
          isProcessing
        }
        className="w-full"
        data-testid="button-confirm-payment"
      >
        {isProcessing
          ? "Processing..."
          : validatedVoucher
          ? `Activate ${validatedVoucher.plan === 'annual' ? 'Annual' : 'Monthly'} Access`
          : validatedDiscount && validatedDiscount.type === 'trial'
          ? `Start ${validatedDiscount.value} Day Free Trial`
          : `Subscribe - ${territory.currency} $${discountedPrice.toLocaleString()}${selectedPlan === 'monthly' ? '/mo' : '/yr'}`
        }
      </Button>

      <Dialog
        open={!!duplicateCardInfo}
        onOpenChange={(open) => { if (!open) setDuplicateCardInfo(null); }}
      >
        <DialogContent className="max-w-md" data-testid="dialog-duplicate-card">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-amber-100 dark:bg-amber-950 flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <DialogTitle>This card is already on another account</DialogTitle>
            </div>
            <DialogDescription className="pt-3">
              We noticed the card you entered is already linked to <span className="font-medium text-foreground">{duplicateCardInfo?.existingAccountMaskedEmail}</span>.
              If this is you signing up an additional child, you can continue. If you meant to use your existing account, log in instead.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2 flex-col sm:flex-col">
            <Button
              type="button"
              className="w-full"
              disabled={isProcessing}
              onClick={async () => {
                if (!duplicateCardInfo) return;
                setIsProcessing(true);
                const setupId = duplicateCardInfo.setupIntentId;
                setDuplicateCardInfo(null);
                try {
                  const data = await postConfirmSubscription(setupId, true);
                  if (data?.success) {
                    trackEvent('Subscription Created', { plan: selectedPlan, grade: selectedGrade, duplicate_card_acknowledged: true });
                    capturePostHogEvent('Subscription Created', { plan: selectedPlan, grade: selectedGrade, duplicate_card_acknowledged: true });
                    await queryClient.invalidateQueries({ queryKey: ['/api/auth/me'] });
                    setShowSuccess(true);
                  } else if (data && !data.success) {
                    toast({
                      title: "Subscription Error",
                      description: data.message || "Failed to activate subscription",
                      variant: "destructive",
                    });
                  }
                } catch (err: any) {
                  toast({
                    title: "Subscription Error",
                    description: err?.message || "Failed to activate subscription",
                    variant: "destructive",
                  });
                } finally {
                  setIsProcessing(false);
                }
              }}
              data-testid="button-duplicate-continue"
            >
              This is a new child, continue
            </Button>
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => { setDuplicateCardInfo(null); navigate('/login'); }}
              data-testid="button-duplicate-login"
            >
              Log into existing account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </form>
  );
};

export default function Subscribe() {
  const { territory } = useTerritory();
  // Fetch grades from API
  const { data: grades = [] } = useQuery<Array<{id: string; gradeNumber: number; name: string; isActive: boolean; sortOrder: number}>>({
    queryKey: ['/api/curriculum/grades'],
  });

  const activeGrades = grades
    .filter(g => g.isActive)
    .sort((a, b) => (a.sortOrder ?? a.gradeNumber) - (b.sortOrder ?? b.gradeNumber));

  const comingSoonGrades = grades
    .filter(g => !g.isActive && [7, 8, 9, 10].includes(g.gradeNumber))
    .sort((a, b) => a.gradeNumber - b.gradeNumber);

  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'yearly' | null>(null);
  const [selectedGrade, setSelectedGrade] = useState<number | null>(null);
  const [clientSecret, setClientSecret] = useState("");
  const [setupIntentId, setSetupIntentId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [stripeStatus, setStripeStatus] = useState<'pending' | 'ready' | 'blocked'>(
    stripePromise ? 'pending' : 'blocked',
  );
  useEffect(() => {
    if (!stripePromise) return;
    stripePromise.then((s) => setStripeStatus(s ? 'ready' : 'blocked'));
  }, []);
  const { toast } = useToast();
  const { user, isLoading: authLoading, logout } = useAuth();

  /**
   * What exam a grade is actually working towards. The territory config carries a single
   * examName ("PEP" in Jamaica) and the page pasted it against whatever grade the user had, so a
   * Grade 11 subscriber, who has ten CSEC subjects from Principles of Accounts to Additional
   * Mathematics, was sold "Grade 11 PEP preparation materials". PEP is the Grade 6 exam.
   */
  const examLabelFor = (grade?: number | null): string => {
    if (grade === 11) return "CSEC";
    if (grade && grade >= 4 && grade <= 6) return territory.examName;
    return "";
  };
  const examLabel = examLabelFor(user?.grade);
  const [, setLocation] = useLocation();

  // Lapsed users land here after login; surface unread EduFocal Bot messages
  // so they can still find outreach sent to them (Messages is open to all signed-in users)
  const { data: unreadData } = useQuery<{ count: number }>({
    queryKey: ['/api/messages/unread-count'],
    enabled: !!user,
  });
  const unreadMessages = unreadData?.count ?? 0;

  // Redirect already-subscribed users (test accounts, renewals) straight to dashboard
  useEffect(() => {
    if (authLoading) return;
    if (!user) return;
    const validStatuses = ['active', 'trial', 'trialing', 'pending'];
    const hasValidStatus = validStatuses.includes(user.subscriptionStatus || '');
    const hasValidEndDate = user.subscriptionEndDate && new Date(user.subscriptionEndDate) > new Date();
    if (hasValidStatus && hasValidEndDate) {
      setLocation('/dashboard');
    }
  }, [user, authLoading, setLocation]);

  const handleSelectPlan = async (plan: 'monthly' | 'yearly', grade: number) => {
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "Please log in to subscribe",
        variant: "destructive",
      });
      setLocation('/login');
      return;
    }

    setIsLoading(true);
    setSelectedPlan(plan);
    setSelectedGrade(grade);
    capturePostHogEvent('Plan Selected', { plan, grade });

    try {
      const response = await apiRequest("POST", "/api/create-subscription", { 
        plan, 
        grade 
      });
      const data = await response.json();
      
      if (data.clientSecret && data.setupIntentId) {
        setClientSecret(data.clientSecret);
        setSetupIntentId(data.setupIntentId);
      } else {
        throw new Error("Failed to initialize payment");
      }
    } catch (error: any) {
      toast({
        title: "Subscription Error",
        description: error.message || "Failed to create subscription",
        variant: "destructive",
      });
      setSelectedPlan(null);
      setSelectedGrade(null);
    } finally {
      setIsLoading(false);
    }
  };

  if (stripeStatus === 'blocked') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle>Payment Processor Blocked</CardTitle>
            <CardDescription>
              Your browser appears to be blocking our payment processor (Stripe). This is usually caused by an ad-blocker, privacy extension, or strict tracking-protection setting.
              <br /><br />
              Please disable any ad-blockers or privacy extensions for <strong>edufocalacademy.com</strong> and reload the page. If the problem persists, try a different browser.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (clientSecret && selectedPlan && selectedGrade && setupIntentId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-chart-2 via-chart-2/90 to-primary p-4 flex items-center justify-center">
        <Card className="w-full max-w-2xl">
          <CardHeader>
            <CardTitle>Complete Your Subscription</CardTitle>
            <CardDescription>
              Grade {selectedGrade} - {selectedPlan === 'monthly' ? 'Monthly Plan' : 'Yearly Plan'}
            </CardDescription>
            {/* Reaching the card form was a one way door: no back, no cancel. Dropping the client
                secret returns to plan selection, which is also where the "not now" exit lives. */}
            <Button
              variant="ghost"
              size="sm"
              className="w-fit -ml-2 mt-1 text-muted-foreground"
              onClick={() => { setClientSecret(""); setSetupIntentId(""); setSelectedPlan(null); }}
              data-testid="button-back-to-plans"
            >
              Back to plans
            </Button>
          </CardHeader>
          <CardContent>
            <Elements 
              stripe={stripePromise} 
              options={{ 
                clientSecret,
                appearance: {
                  theme: 'stripe',
                },
                loader: 'auto',
              }}
            >
              <SubscribeForm 
                selectedPlan={selectedPlan} 
                selectedGrade={selectedGrade}
                setupIntentId={setupIntentId}
                onSuccess={() => setLocation('/dashboard')}
              />
            </Elements>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-chart-2 via-chart-2/90 to-primary p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        {unreadMessages > 0 && (
          <div className="bg-white rounded-lg p-4 mb-8 flex items-center justify-between gap-4 shadow-md" data-testid="banner-unread-messages">
            <p className="text-sm md:text-base font-medium text-foreground">
              You have {unreadMessages === 1 ? 'a new message' : `${unreadMessages} new messages`} from EduFocal Academy
            </p>
            <Button onClick={() => setLocation('/messages')} data-testid="button-read-messages">
              Read {unreadMessages === 1 ? 'it' : 'them'}
            </Button>
          </div>
        )}
        <div className="text-center mb-12 text-white">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="text-pricing-title">
            Complete Your Subscription
          </h1>
          <p className="text-xl text-white/90 mb-2" data-testid="text-pricing-subtitle">
            Choose a plan to access all Grade {user?.grade || ''} {examLabel ? `${examLabel} ` : ''}learning materials
          </p>
          <p className="text-white/80" data-testid="text-pricing-note">
            Payment required to access learning content and features
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {/* Monthly Plan */}
          <Card className="relative">
            <CardHeader>
              <CardTitle className="text-2xl">Monthly Plan</CardTitle>
              <CardDescription>Perfect for short-term preparation</CardDescription>
              <div className="mt-4">
                <span className="text-4xl font-bold">{territory.currency} {territory.monthlyPrice}</span>
                <span className="text-muted-foreground">/month</span>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span>30 days of full access</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span>Every subject for your grade</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span>Practice tests & quizzes</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span>Progress tracking & analytics</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span>Gamified learning experience</span>
                </li>
              </ul>
            </CardContent>
            <CardFooter className="flex flex-col gap-3">
              {activeGrades.map((g) => (
                <Button
                  key={g.id}
                  onClick={() => handleSelectPlan('monthly', g.gradeNumber)}
                  disabled={isLoading}
                  className="w-full"
                  variant={user?.grade === g.gradeNumber ? "default" : "outline"}
                  data-testid={`button-subscribe-monthly-grade-${g.gradeNumber}`}
                >
                  {isLoading ? "Loading..." : `${g.name}${user?.grade === g.gradeNumber ? ' (Your Grade)' : ''}`}
                </Button>
              ))}
              {comingSoonGrades.map((g) => (
                <Button
                  key={g.id}
                  disabled
                  className="w-full opacity-50"
                  variant="outline"
                >
                  {g.displayName} (Coming Soon)
                </Button>
              ))}
            </CardFooter>
          </Card>

          {/* Yearly Plan */}
          <Card className="relative border-2 border-primary">
            <Badge className="absolute -top-3 left-1/2 -translate-x-1/2" data-testid="badge-best-value">
              Best Value - Save 67%
            </Badge>
            <CardHeader>
              <CardTitle className="text-2xl">Yearly Plan</CardTitle>
              <CardDescription>Best for complete {examLabel || 'exam'} preparation</CardDescription>
              <div className="mt-4">
                <span className="text-4xl font-bold">{territory.currency} {territory.yearlyPrice}</span>
                <span className="text-muted-foreground">/year</span>
              </div>
              <p className="text-sm text-muted-foreground">
                {territory.yearlySavings}
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="font-semibold">Full year of access</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span>Every subject for your grade</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span>Practice tests & quizzes</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span>Progress tracking & analytics</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span>Gamified learning experience</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="font-semibold">Priority support</span>
                </li>
              </ul>
            </CardContent>
            <CardFooter className="flex flex-col gap-3">
              {activeGrades.map((g) => (
                <Button
                  key={g.id}
                  onClick={() => handleSelectPlan('yearly', g.gradeNumber)}
                  disabled={isLoading}
                  className="w-full"
                  variant={user?.grade === g.gradeNumber ? "default" : "outline"}
                  data-testid={`button-subscribe-yearly-grade-${g.gradeNumber}`}
                >
                  {isLoading ? "Loading..." : `${g.name}${user?.grade === g.gradeNumber ? ' (Your Grade)' : ''}`}
                </Button>
              ))}
              {comingSoonGrades.map((g) => (
                <Button
                  key={g.id}
                  disabled
                  className="w-full opacity-50"
                  variant="outline"
                >
                  {g.displayName} (Coming Soon)
                </Button>
              ))}
            </CardFooter>
          </Card>
        </div>

        {/* A way out. Someone who signed up and then decided not to pay had no exit from this
            screen at all: no back, no log out, no "not now". They were simply stuck, and the only
            escape was closing the tab, which leaves them unable to reach their own account next
            time without hitting the same wall. */}
        <div className="text-center mb-8">
          <Button
            variant="ghost"
            className="text-white/90 hover:text-white hover:bg-white/10"
            onClick={async () => { try { await logout(); } finally { setLocation('/'); } }}
            data-testid="button-subscribe-not-now"
          >
            Not now, sign me out
          </Button>
          <p className="text-white/70 text-xs mt-2">
            Your account stays as it is. Sign back in whenever you are ready.
          </p>
        </div>

        <div className="text-center text-white/80 text-sm">
          <p>All plans include access to all subjects for your selected grade level</p>
          <p className="mt-2">Secure payment processing powered by Stripe</p>
        </div>
      </div>
    </div>
  );
}
